<template>
  <div class="research-map-wrapper">
    <!-- Header Controls & Evidence Scale Dashboard -->
    <div class="map-controls-header">
      <!-- Filter Dropdowns Row -->
      <div class="filters-row">
        <!-- Musty Damp Odor Dropdown -->
        <div class="control-card" title="Filter kits by customer-reported Musty Damp Odor answer">
          <label class="control-label">
            <span class="shape-icon circle">●</span> Musty Odor
          </label>
          <select v-model="filters.musty_damp_odor" class="filter-select">
            <option value="Any">Any (All)</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Skipped">Skipped</option>
          </select>
        </div>

        <!-- Visible Mold Dropdown -->
        <div class="control-card" title="Filter kits by customer-reported Visible Mold answer">
          <label class="control-label">
            <span class="shape-icon square">■</span> Visible Mold
          </label>
          <select v-model="filters.visible_mold" class="filter-select">
            <option value="Any">Any (All)</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Skipped">Skipped</option>
          </select>
        </div>

        <!-- Symptoms Dropdown -->
        <div class="control-card" title="Filter kits by customer-reported Symptoms answer">
          <label class="control-label">
            <span class="shape-icon triangle">▲</span> Symptoms
          </label>
          <select v-model="filters.symptoms" class="filter-select">
            <option value="Any">Any (All)</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Skipped">Skipped</option>
          </select>
        </div>

        <!-- Action Controls: Reset & Matching Badge -->
        <div class="control-actions">
          <button
            type="button"
            class="reset-btn"
            @click="resetFilters"
            title="Reset all survey filters to Any"
          >
            <span class="reset-icon">🔄</span> Reset Filters
          </button>
          <div
            class="matching-badge"
            title="Total unique kits matching the active filters on map"
          >
            <span class="badge-label">Matching Kits:</span>
            <span class="badge-count">{{ filteredKits.length }}</span>
          </div>
        </div>
      </div>

      <!-- Lab Severity Results & Percentage Breakdown Bar -->
      <div class="evidence-scale-container">
        <div class="scale-header">
          <span class="scale-title">Laboratory Risk Distribution</span>
          <span class="scale-subtitle" v-if="filteredKits.length > 0">
            Based on highest indoor severity found per kit
          </span>
        </div>
        <div class="evidence-bars">
          <div
            v-for="level in evidenceLevels"
            :key="level.key"
            class="evidence-bar-item"
            :class="{ active: selectedRiskFilter === level.key }"
            @click="toggleRiskFilter(level.key)"
            :title="'Click to toggle filtering by ' + level.label"
          >
            <div
              class="evidence-color-bar"
              :style="{ backgroundColor: level.color }"
            >
              <span class="evidence-count">{{ evidenceCounts[level.key] || 0 }}</span>
            </div>
            <div class="evidence-meta">
              <span class="evidence-label">{{ level.label }}</span>
              <span class="evidence-percentage">
                {{ getPercentage(level.key) }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map Container -->
    <div id="map2" class="map-instance"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { mapGetters, mapActions } from "vuex";
import { parseAndConvertKitId } from "../../mixins/parseAndConvertKitId.js";

const EVIDENCE_LEVELS = [
  { key: "green", label: "Not Evident", color: "#5cb85c", level: 0 },
  { key: "yellow", label: "Slightly Evident", color: "#f1c40f", level: 1 },
  { key: "orange", label: "Moderately Evident", color: "#ff9800", level: 2 },
  { key: "red", label: "Significantly Evident", color: "#f53b57", level: 3 },
  { key: "grey", label: "Problem", color: "#808080", level: 4 },
  { key: "blue", label: "Not Completed", color: "#005eb8", level: 5 }
];

function parseRiskValue(val) {
  if (val === undefined || val === null || val === "") return -1;
  if (typeof val === "number") {
    if (val >= 0 && val <= 3) return val;
    return -1;
  }
  const str = String(val).trim().toLowerCase();
  if (str === "0" || str.includes("not evident") || str.includes("not_evident") || str === "green") return 0;
  if (str === "1" || str.includes("slightly evident") || str.includes("slightly_evident") || str === "yellow") return 1;
  if (str === "2" || str.includes("moderately evident") || str.includes("moderately_evident") || str === "orange") return 2;
  if (str === "3" || str.includes("significantly evident") || str.includes("significantly_evident") || str === "red") return 3;
  const num = Number(str);
  if (!isNaN(num) && num >= 0 && num <= 3) return num;
  return -1;
}

function isKitAnalysisCompleted(item) {
  if (!item) return false;

  // Strict Rule: Only test kits with status 5 (Analysis Completed) represent completed kits
  if (item.status === 5 || item.status === "5") return true;
  if (item.status !== undefined && item.status !== null) {
    const s = String(item.status).trim().toLowerCase().replace(/\s+/g, "_");
    if (s === "analysis_completed" || s === "completed") return true;
  }

  return false;
}

function isKitProblematic(item) {
  if (!item) return false;
  const s = item.status;
  if (s === 6 || s === "6" || s === 10 || s === "10" || s === 11 || s === "11") return true;
  if (s !== undefined && s !== null) {
    const str = String(s).trim().toLowerCase().replace(/\s+/g, "_");
    if (
      str === "problem" ||
      str === "analysis_failed" ||
      str === "partially_failed"
    ) return true;
  }
  return false;
}

function getHighestIndoorRiskLevel(item) {
  if (!item) return null;

  // Statuses 6, 10, and 11 are classified as Problematic (grey)
  if (isKitProblematic(item)) {
    return EVIDENCE_LEVELS[4];
  }

  // Kits with laboratory process not completed yet are classified as Not Completed (blue)
  if (!isKitAnalysisCompleted(item)) {
    return EVIDENCE_LEVELS[5];
  }

  let sampleList = [];
  let rep = item.report;
  if (Array.isArray(rep)) rep = rep[0];

  if (rep) {
    if (Array.isArray(rep.reportSamples)) {
      sampleList = rep.reportSamples;
    } else if (Array.isArray(rep.report_samples)) {
      sampleList = rep.report_samples;
    }
  }
  if (!sampleList || sampleList.length === 0) {
    if (Array.isArray(item.reportSamples)) {
      sampleList = item.reportSamples;
    } else if (Array.isArray(item.report_samples)) {
      sampleList = item.report_samples;
    }
  }

  let maxRisk = -1;

  if (sampleList && sampleList.length > 0) {
    sampleList.forEach((sample) => {
      const loc = String(sample.location || "").toLowerCase();
      const isOutdoor =
        Number(sample.type) === 1 ||
        loc.includes("outside") ||
        loc.includes("outdoor") ||
        loc.includes("control");

      if (!isOutdoor) {
        let rawVal = sample.rank !== undefined && sample.rank !== null
          ? sample.rank
          : (sample.status !== undefined && sample.status !== null ? sample.status : (sample.result !== undefined && sample.result !== null ? sample.result : sample.Rank));

        const parsed = parseRiskValue(rawVal);
        if (parsed > maxRisk) {
          maxRisk = parsed;
        }
      }
    });
  }

  if (maxRisk < 0) {
    let rawKitResult = parseRiskValue(item.result);
    if (rawKitResult < 0 && rep) {
      rawKitResult = parseRiskValue(rep.result);
    }
    if (rawKitResult >= 0) {
      maxRisk = rawKitResult;
    }
  }

  if (maxRisk >= 1 && maxRisk <= 3) {
    return EVIDENCE_LEVELS[maxRisk];
  }

  // Any completed kit (status 5) without elevated indoor mold (maxRisk <= 0) is "Not Evident" (green)
  return EVIDENCE_LEVELS[0];
}

export default {
  name: "Map2",
  mixins: [parseAndConvertKitId],
  data() {
    return {
      map: null,
      markerClusterGroup: null,
      evidenceLevels: EVIDENCE_LEVELS,
      filterLogic: "AND",
      selectedRiskFilter: null,
      filters: {
        musty_damp_odor: "Any",
        visible_mold: "Any",
        symptoms: "Any"
      }
    };
  },
  computed: {
    ...mapGetters(["getAnalyticsAll"]),
    allUniqueKits() {
      const rawRows = Object.values(this.getAnalyticsAll || {});
      const kitMap = new Map();
      for (const item of rawRows) {
        const uniqueId = item.id || item.kit_id;
        if (uniqueId && !kitMap.has(uniqueId)) {
          kitMap.set(uniqueId, item);
        }
      }
      return Array.from(kitMap.values());
    },
    applicableKitsForRisk() {
      if (!this.selectedRiskFilter) {
        return this.allUniqueKits;
      }
      return this.allUniqueKits.filter(item => {
        const riskInfo = getHighestIndoorRiskLevel(item);
        return riskInfo && riskInfo.key === this.selectedRiskFilter;
      });
    },
    kitsMatchingSurveyFilters() {
      let uniqueKits = this.allUniqueKits;
      const { musty_damp_odor, visible_mold, symptoms } = this.filters;
      const activeFilters = [];

      if (musty_damp_odor !== "Any") {
        activeFilters.push(item =>
          this.checkCondition(item.musty_damp_odor, musty_damp_odor)
        );
      }
      if (visible_mold !== "Any") {
        activeFilters.push(item =>
          this.checkCondition(item.visible_mold, visible_mold)
        );
      }
      if (symptoms !== "Any") {
        activeFilters.push(item =>
          this.checkCondition(item.symptoms, symptoms)
        );
      }

      if (activeFilters.length > 0) {
        if (this.filterLogic === "AND") {
          uniqueKits = uniqueKits.filter(item => activeFilters.every(fn => fn(item)));
        } else {
          uniqueKits = uniqueKits.filter(item => activeFilters.some(fn => fn(item)));
        }
      }

      return uniqueKits;
    },
    filteredKits() {
      let uniqueKits = this.kitsMatchingSurveyFilters;

      if (this.selectedRiskFilter) {
        uniqueKits = uniqueKits.filter(item => {
          const riskInfo = getHighestIndoorRiskLevel(item);
          return riskInfo && riskInfo.key === this.selectedRiskFilter;
        });
      }

      return uniqueKits.map(item => {
        const riskInfo = getHighestIndoorRiskLevel(item);
        return {
          ...item,
          riskKey: riskInfo ? riskInfo.key : "blue",
          riskLabel: riskInfo ? riskInfo.label : "N/A"
        };
      });
    },
    evidenceCounts() {
      const counts = { green: 0, yellow: 0, orange: 0, red: 0, grey: 0, blue: 0 };
      for (const kit of this.kitsMatchingSurveyFilters) {
        const riskInfo = getHighestIndoorRiskLevel(kit);
        if (riskInfo && counts[riskInfo.key] !== undefined) {
          counts[riskInfo.key]++;
        }
      }
      return counts;
    }
  },
  watch: {
    getAnalyticsAll: {
      handler() {
        this.emitSurveyCounts();
        this.updateMarkers();
      },
      deep: true,
      immediate: true
    },
    filteredKits() {
      this.emitSurveyCounts();
      this.updateMarkers();
    },
    filters: {
      handler() {
        this.emitSurveyCounts();
        this.updateMarkers();
      },
      deep: true
    },
    selectedRiskFilter() {
      this.emitSurveyCounts();
      this.updateMarkers();
    }
  },
  mounted() {
    this.initMap();

    this.markerClusterGroup = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: cluster => {
        const children = cluster.getAllChildMarkers();
        const count = cluster.getChildCount();

        const colorCounts = { green: 0, yellow: 0, orange: 0, red: 0, grey: 0, blue: 0 };

        children.forEach(marker => {
          if (!marker.options) return;
          const c = marker.options.markerColor || "blue";
          if (colorCounts[c] !== undefined) colorCounts[c]++;
        });

        let mainTextFontSize, baseIconDiameter;
        if (count < 10) {
          baseIconDiameter = 32;
          mainTextFontSize = 13;
        } else if (count < 100) {
          baseIconDiameter = 38;
          mainTextFontSize = 14;
        } else if (count < 1000) {
          baseIconDiameter = 44;
          mainTextFontSize = 14;
        } else {
          baseIconDiameter = 52;
          mainTextFontSize = 13;
        }

        let iconDiameter = Math.round(
          Math.max(
            baseIconDiameter,
            String(count).length * mainTextFontSize * 0.65 + 10,
            24
          )
        );
        iconDiameter = Math.min(iconDiameter, 65);
        const radius = iconDiameter / 2 - 1.5;
        const centerX = iconDiameter / 2;
        const centerY = iconDiameter / 2;

        let piePaths = "";
        if (count > 0) {
          let currentAngle = 0;
          const colorMap = {
            green: "#5cb85c",
            yellow: "#f1c40f",
            orange: "#ff9800",
            red: "#f53b57",
            grey: "#808080",
            blue: "#005eb8"
          };

          const order = ["green", "yellow", "orange", "red", "grey", "blue"];
          order.forEach(cKey => {
            const cCount = colorCounts[cKey];
            if (cCount > 0) {
              const sweepAngle = (cCount / count) * 360;
              if (sweepAngle >= 359.9) {
                piePaths += `<circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${colorMap[cKey]}"/>`;
              } else {
                const path = this.describeSector(
                  centerX,
                  centerY,
                  radius,
                  currentAngle,
                  currentAngle + sweepAngle
                );
                piePaths += `<path d="${path}" fill="${colorMap[cKey]}"/>`;
              }
              currentAngle += sweepAngle;
            }
          });
        }

        const innerRadius = radius * 0.58;
        const svgHtml = `<svg width="${iconDiameter}" height="${iconDiameter}" viewBox="0 0 ${iconDiameter} ${iconDiameter}" xmlns="http://www.w3.org/2000/svg"><g>${piePaths}<circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" fill="white" stroke="rgba(0,0,0,0.15)"/><text x="${centerX}" y="${centerY}" font-family="-apple-system,sans-serif" font-size="${mainTextFontSize}px" font-weight="bold" fill="#333" text-anchor="middle" dominant-baseline="central">${count}</text></g></svg>`;

        let tooltipParts = [`<strong>Total Kits: ${count}</strong>`];
        tooltipParts.push(
          '<hr style="margin:4px 0;border-top:1px solid #ddd;">'
        );
        EVIDENCE_LEVELS.forEach(lvl => {
          const num = colorCounts[lvl.key];
          if (num > 0) {
            tooltipParts.push(
              `<span style="color:${lvl.color};font-weight:bold;">${
                lvl.label
              }: ${num}</span>`
            );
          }
        });

        cluster.bindTooltip(
          `<div style="font-size:12.5px;line-height:1.7;">${tooltipParts.join(
            "<br>"
          )}</div>`
        );
        return L.divIcon({
          html: svgHtml,
          className: "marker-cluster",
          iconSize: [iconDiameter, iconDiameter]
        });
      }
    });

    this.map.addLayer(this.markerClusterGroup);
    this.addLegend();
    this.sendSearchReqAnalyticsAll();
    this.emitSurveyCounts();
    this.updateMarkers();
  },
  methods: {
    ...mapActions(["sendSearchReqAnalyticsAll"]),
    getPercentage(key) {
      const total = Object.values(this.evidenceCounts).reduce((a, b) => a + b, 0);
      if (!total) return "0.0";
      const val = this.evidenceCounts[key] || 0;
      return ((val / total) * 100).toFixed(1);
    },

    toggleSurveyFilter(type, response) {
      if (this.filters[type] === response) {
        this.filters[type] = "Any";
      } else {
        this.filters[type] = response;
      }
      this.emitSurveyCounts();
      this.updateMarkers();
    },

    toggleRiskFilter(key) {
      if (this.selectedRiskFilter === key) {
        this.selectedRiskFilter = null;
      } else {
        this.selectedRiskFilter = key;
      }
      this.emitSurveyCounts();
      this.updateMarkers();
    },

    emitSurveyCounts() {
      const counts = {
        visible_mold: { checked: 0, unchecked: 0, null: 0, total: 0 },
        musty_damp_odor: { checked: 0, unchecked: 0, null: 0, total: 0 },
        symptoms: { checked: 0, unchecked: 0, null: 0, total: 0 }
      };

      const targetKits = this.applicableKitsForRisk;
      for (const kit of targetKits) {
        ["visible_mold", "musty_damp_odor", "symptoms"].forEach(type => {
          const val = kit[type];
          counts[type].total++;
          if (this.checkCondition(val, "Yes")) {
            counts[type].checked++;
          } else if (this.checkCondition(val, "No")) {
            counts[type].unchecked++;
          } else {
            counts[type].null++;
          }
        });
      }

      this.$emit("update-survey-counts", counts);
      this.$emit("update-filters", this.filters);
      this.$emit("update-map-state", {
        filteredKits: this.filteredKits,
        selectedRiskFilter: this.selectedRiskFilter,
        filters: this.filters
      });
    },

    checkCondition(fieldVal, filterVal) {
      const isTrue =
        fieldVal === true ||
        fieldVal === 1 ||
        fieldVal === "1" ||
        fieldVal === "yes" ||
        fieldVal === "Yes";
      const isFalse =
        fieldVal === false ||
        fieldVal === 0 ||
        fieldVal === "0" ||
        fieldVal === "no" ||
        fieldVal === "No";
      const isSkipped =
        fieldVal === null ||
        fieldVal === undefined ||
        fieldVal === "" ||
        (!isTrue && !isFalse);

      if (filterVal === "Yes") return isTrue;
      if (filterVal === "No") return isFalse;
      if (filterVal === "Skipped") return isSkipped;
      return true;
    },

    resetFilters() {
      this.filters = {
        musty_damp_odor: "Any",
        visible_mold: "Any",
        symptoms: "Any"
      };
      this.filterLogic = "AND";
      this.selectedRiskFilter = null;
      this.emitSurveyCounts();
      this.updateMarkers();
    },

    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
      const a = ((angleInDegrees - 90) * Math.PI) / 180;
      return {
        x: centerX + radius * Math.cos(a),
        y: centerY + radius * Math.sin(a)
      };
    },

    describeSector(x, y, radius, startAngle, endAngle) {
      if (endAngle - startAngle >= 360) {
        endAngle = 359.99;
      }
      const s = this.polarToCartesian(x, y, radius, startAngle);
      const e = this.polarToCartesian(x, y, radius, endAngle);
      const l = endAngle - startAngle <= 180 ? "0" : "1";
      return [
        "M",
        x,
        y,
        "L",
        s.x,
        s.y,
        "A",
        radius,
        radius,
        0,
        l,
        1,
        e.x,
        e.y,
        "Z"
      ].join(" ");
    },

    initMap() {
      const mapEl = document.getElementById("map2");
      if (!mapEl) return;
      this.map = L.map("map2").setView([39.8283, -98.5795], 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors"
      }).addTo(this.map);
    },

    addLegend() {
      const legend = L.control({ position: "bottomright" });

      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "legend");
        L.DomEvent.disableClickPropagation(div).disableScrollPropagation(div);

        const sampleSvg = `<svg width="36" height="36" viewBox="0 0 36 36"><path d="${this.describeSector(
          18,
          18,
          16,
          0,
          90
        )}" fill="#5cb85c"/><path d="${this.describeSector(
          18,
          18,
          16,
          90,
          180
        )}" fill="#f1c40f"/><path d="${this.describeSector(
          18,
          18,
          16,
          180,
          270
        )}" fill="#ff9800"/><path d="${this.describeSector(
          18,
          18,
          16,
          270,
          360
        )}" fill="#f53b57"/><circle cx="18" cy="18" r="9" fill="white"/><text x="18" y="18" font-size="12px" font-weight="bold" fill="#333" text-anchor="middle" dominant-baseline="central">XX</text></svg>`;

        let colorItemsHtml = "";
        EVIDENCE_LEVELS.forEach(lvl => {
          colorItemsHtml += `<div class="legend-item"><div class="legend-color-box" style="background:${
            lvl.color
          }"></div><span>${lvl.label}</span></div>`;
        });

        div.innerHTML = `
          <div class="legend-header">
            <h4>Risk Legend</h4>
            <button type="button" class="legend-toggle" aria-label="Toggle legend" title="Toggle legend">
              <span class="toggle-icon">–</span>
            </button>
          </div>
          <div class="legend-content">
            <div class="legend-section">
              <strong>Highest Indoor Risk Levels</strong>
              ${colorItemsHtml}
            </div>
            <div class="legend-section">
              <strong>Clusters</strong>
              <div class="legend-cluster-example">${sampleSvg}<div class="legend-cluster-desc"><strong>Number:</strong> Kits<br><strong>Ring:</strong> Risk Ratio</div></div>
            </div>
          </div>
        `;

        const toggleButton = div.querySelector(".legend-toggle");
        toggleButton.addEventListener("click", e => {
          const legendEl = e.currentTarget.closest(".legend");
          legendEl.classList.toggle("minimized");
          const icon = e.currentTarget.querySelector(".toggle-icon");
          icon.textContent = legendEl.classList.contains("minimized")
            ? "+"
            : "–";
        });
        return div;
      };
      legend.addTo(this.map);
    },

    updateMarkers() {
      if (!this.markerClusterGroup) return;
      this.markerClusterGroup.clearLayers();
      const kits = this.filteredKits;
      if (!kits.length) return;

      const markersToAdd = [];
      const formatBadge = val => {
        if (val === true || val === 1 || val === "1") {
          return `<span style="background:#def7ec;color:#03543f;padding:1px 7px;border-radius:10px;font-weight:700;font-size:11px;">Yes</span>`;
        }
        if (val === false || val === 0 || val === "0") {
          return `<span style="background:#f3f4f6;color:#4b5563;padding:1px 7px;border-radius:10px;font-weight:600;font-size:11px;">No</span>`;
        }
        return `<span style="background:#f3f4f6;color:#9ca3af;padding:1px 7px;border-radius:10px;font-weight:500;font-size:11px;">N/A</span>`;
      };

      for (const kit of kits) {
        let lat = null;
        let lng = null;

        let coords = kit.coordinates;
        if (Array.isArray(coords)) {
          coords = coords[0];
        }

        if (coords) {
          if (coords.latitude !== undefined && coords.longitude !== undefined) {
            lat = parseFloat(coords.latitude);
            lng = parseFloat(coords.longitude);
          } else if (coords.lat !== undefined && coords.lng !== undefined) {
            lat = parseFloat(coords.lat);
            lng = parseFloat(coords.lng);
          }
        }
        if (lat === null || isNaN(lat)) {
          if (kit.latitude !== undefined && kit.longitude !== undefined) {
            lat = parseFloat(kit.latitude);
            lng = parseFloat(kit.longitude);
          } else if (kit.lat !== undefined && kit.lng !== undefined) {
            lat = parseFloat(kit.lat);
            lng = parseFloat(kit.lng);
          }
        }

        if (lat === null || lng === null || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) continue;

        const riskInfo = getHighestIndoorRiskLevel(kit);
        const markerColorKey = riskInfo ? riskInfo.key : "blue";
        const riskDisplayLabel = riskInfo ? riskInfo.label : "N/A";
        const riskDisplayColor = riskInfo ? riskInfo.color : "#005eb8";

        const tooltipContent = `
          <div style="font-size:12.5px;line-height:1.7;min-width:160px;font-family:-apple-system,sans-serif;">
            <div style="font-size:13px;font-weight:700;color:#0f172a;margin-bottom:4px;">Kit #${this.convertKitId ? this.convertKitId(kit.kit_id) : (kit.kit_id || "N/A")}</div>
            <div style="margin-bottom:6px;">
              <span style="font-size:11px;text-transform:uppercase;color:#64748b;font-weight:600;display:block;">Laboratory Risk</span>
              <span style="background:${riskDisplayColor};color:#ffffff;padding:2px 8px;border-radius:12px;font-weight:700;font-size:11.5px;display:inline-block;margin-top:2px;">
                ${riskDisplayLabel}
              </span>
            </div>
            <div style="border-top:1px solid #e2e8f0;padding-top:6px;margin-top:6px;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="color:#475569;font-size:11.5px;"><span style="color:#555;">●</span> Musty Odor</span>
                ${formatBadge(kit.musty_damp_odor)}
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="color:#475569;font-size:11.5px;"><span style="color:#555;">■</span> Visible Mold</span>
                ${formatBadge(kit.visible_mold)}
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <span style="color:#475569;font-size:11.5px;"><span style="color:#555;">▲</span> Symptoms</span>
                ${formatBadge(kit.symptoms)}
              </div>
            </div>
          </div>
        `;

        const marker = L.marker([lat, lng], {
          icon: L.divIcon({
            html: `<div class="custom-marker circle ${markerColorKey}"></div>`,
            className: "",
            iconSize: [18, 18],
            iconAnchor: [9, 9]
          }),
          markerColor: markerColorKey
        }).bindTooltip(tooltipContent);

        markersToAdd.push(marker);
      }

      if (markersToAdd.length > 0) {
        this.markerClusterGroup.addLayers(markersToAdd);
        if (this.map) {
          this.$nextTick(() => {
            this.map.invalidateSize();
            try {
              const bounds = L.featureGroup(markersToAdd).getBounds();
              if (bounds && bounds.isValid()) {
                this.map.fitBounds(bounds.pad(0.15), { maxZoom: 12 });
              }
            } catch (e) {}
          });
        }
      }
    }
  }
};
</script>

<style scoped>
.research-map-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 10px;
}

.map-controls-header {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
}

.control-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 6px 14px;
  transition: all 0.2s ease;
}

.control-card:hover {
  background: #ffffff;
  border-color: #94a3b8;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.control-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.shape-icon {
  font-size: 13px;
}

.filter-select {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.filter-select:focus {
  border-color: #005eb8;
  box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.15);
}

.control-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reset-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.reset-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #94a3b8;
  transform: translateY(-1px);
}

.matching-badge {
  background: linear-gradient(135deg, #005eb8 0%, #004494 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 16px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 6px rgba(0, 94, 184, 0.25);
}

.badge-label {
  opacity: 0.9;
}

.badge-count {
  font-weight: 800;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  padding: 1px 8px;
  border-radius: 10px;
}

.evidence-scale-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scale-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.scale-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.scale-subtitle {
  font-size: 12px;
  color: #64748b;
}

.evidence-bars {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

@media (max-width: 900px) {
  .evidence-bars {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .evidence-bars {
    grid-template-columns: repeat(2, 1fr);
  }
}

.evidence-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  transition: all 0.2s ease;
}

.evidence-bar-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.evidence-bar-item.active {
  background: #f0f9ff;
  border-color: #005eb8;
  box-shadow: 0 2px 8px rgba(0, 94, 184, 0.15);
}

.evidence-color-bar {
  width: 100%;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s ease;
}

.evidence-bar-item:hover .evidence-color-bar {
  transform: scaleY(1.06);
}

.evidence-count {
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.evidence-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.evidence-label {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  text-align: center;
}

.evidence-percentage {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.map-instance {
  height: 75vh;
  width: 100%;
  border-radius: 12px;
  z-index: 0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  border: 1px solid #cbd5e1;
}
</style>

<style>
.marker-cluster {
  background-color: transparent !important;
  border: none !important;
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.25))
    drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.15));
  transition: transform 0.15s cubic-bezier(0.25, 0.1, 0.25, 1);
}
.marker-cluster:hover {
  transform: scale(1.08);
}
.marker-cluster div {
  display: none !important;
}

/* Custom Marker Colors */
.custom-marker {
  width: 18px;
  height: 18px;
  display: inline-block;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.35);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.custom-marker.blue {
  background-color: #005eb8;
  border-color: #004080;
}
.custom-marker.green {
  background-color: #5cb85c;
  border-color: #449d44;
}
.custom-marker.yellow {
  background-color: #f1c40f;
  border-color: #d4ac0d;
}
.custom-marker.orange {
  background-color: #ff9800;
  border-color: #e68a00;
}
.custom-marker.red {
  background-color: #f53b57;
  border-color: #d6304a;
}
.custom-marker.grey {
  background-color: #808080;
  border-color: #555555;
}

/* Legend styles */
.leaflet-control.legend {
  background: rgba(255, 255, 255, 0.96);
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: #333;
  max-width: 280px;
}
.legend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}
.legend h4 {
  margin: 0;
  font-size: 1.05em;
  font-weight: 700;
  color: #1e293b;
}
.legend-toggle {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  cursor: pointer;
  width: 22px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
}
.legend-toggle:hover {
  background-color: #e2e8f0;
}
.toggle-icon {
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  color: #475569;
}
.legend-content {
  padding: 10px;
  max-height: 400px;
  overflow: hidden;
  transition: max-height 0.3s ease-out, padding 0.3s ease-out;
}
.legend .legend-section {
  margin-bottom: 8px;
}
.legend .legend-section:last-child {
  margin-bottom: 0;
}
.legend strong {
  display: block;
  font-size: 0.9em;
  margin-bottom: 5px;
  padding-bottom: 2px;
  border-bottom: 1px solid #f1f5f9;
  color: #475569;
}
.legend .legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 0.85em;
}
.legend .legend-color-box {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  flex-shrink: 0;
  border-radius: 3px;
}
.legend .legend-cluster-example {
  display: flex;
  align-items: center;
  margin-top: 5px;
}
.legend .legend-cluster-example svg {
  margin-right: 10px;
  flex-shrink: 0;
}
.legend .legend-cluster-desc {
  font-size: 0.85em;
  line-height: 1.3;
}
.legend.minimized .legend-content {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
}
</style>
